import {
    SampleMetricsStackBy,
    SampleMetricsGranularity,
    SampleMetricsTimeRange
} from 'src/app/constants/sample-metrics.constants';
import { SampleShiftCoverage } from 'src/app/enums/sampleShiftType.enum';
import { getEnumLookup } from 'src/app/_helpers/enum-helper';
import { allDatesFloor } from '../_shared/dashboard-widget.utils';

export interface SampleCollectionMetricsFilterValues {
    selectedAgentGroupId: string | null;
    selectedStackBy: SampleMetricsStackBy;
    selectedGranularity: SampleMetricsGranularity;
    selectedTimeRange: SampleMetricsTimeRange;
    customStartDate: string | null;
    customEndDate: string | null;
    selectedShiftType: SampleShiftCoverage | null;
    selectedAgentIds: string[];
    selectedSegIds: string[];
    selectedSurveyTypeIds: string[];
}

export const SAMPLE_METRICS_FILTER_DEFAULTS: SampleCollectionMetricsFilterValues = {
    selectedAgentGroupId: null,
    selectedStackBy: SampleMetricsStackBy.Agent,
    selectedGranularity: SampleMetricsGranularity.Monthly,
    selectedTimeRange: SampleMetricsTimeRange.Ytd,
    customStartDate: null,
    customEndDate: null,
    selectedShiftType: null,
    selectedAgentIds: [],
    selectedSegIds: [],
    selectedSurveyTypeIds: []
};

export const SAMPLE_METRICS_LEGEND_ROW_HEIGHT = 22;
export const SAMPLE_METRICS_LEGEND_VISIBLE_ROWS = 3;
export const SAMPLE_METRICS_LEGEND_MAX_HEIGHT = SAMPLE_METRICS_LEGEND_VISIBLE_ROWS * SAMPLE_METRICS_LEGEND_ROW_HEIGHT;
export const SAMPLE_METRICS_LEGEND_LABEL_MAX_WIDTH = 200;
export const SAMPLE_METRICS_TOOLTIP_FILL = 0x2d3436;

export const STACK_BY_OPTIONS = getEnumLookup(SampleMetricsStackBy);
export const GRANULARITY_OPTIONS = getEnumLookup(SampleMetricsGranularity);
export const SHIFT_TYPE_OPTIONS = getEnumLookup(SampleShiftCoverage);

export const TIME_RANGE_OPTIONS: { id: number; name: string }[] = [
    { id: SampleMetricsTimeRange.AllDates, name: 'All Dates' },
    { id: SampleMetricsTimeRange.Ytd, name: 'Year to Date' },
    { id: SampleMetricsTimeRange.Last12Months, name: 'Last 12 Months' },
    { id: SampleMetricsTimeRange.Last24Months, name: 'Last 24 Months' },
    { id: SampleMetricsTimeRange.Last3Years, name: 'Last 3 Years' },
    { id: SampleMetricsTimeRange.Last5Years, name: 'Last 5 Years' },
    { id: SampleMetricsTimeRange.Custom, name: 'Custom' }
];

export function resolveTimeRangeDates(range: SampleMetricsTimeRange, now: Date = new Date()): { start: Date; end: Date } | null {
    const currentYear = now.getFullYear();
    switch (range) {
        case SampleMetricsTimeRange.Ytd:
            return { start: new Date(currentYear, 0, 1), end: now };
        case SampleMetricsTimeRange.Last12Months: {
            const start = new Date(now);
            start.setMonth(start.getMonth() - 12);
            return { start, end: now };
        }
        case SampleMetricsTimeRange.Last24Months: {
            const start = new Date(now);
            start.setMonth(start.getMonth() - 24);
            return { start, end: now };
        }
        case SampleMetricsTimeRange.Last3Years:
            return { start: new Date(currentYear - 3, 0, 1), end: new Date(currentYear - 1, 11, 31) };
        case SampleMetricsTimeRange.Last5Years:
            return { start: new Date(currentYear - 5, 0, 1), end: new Date(currentYear - 1, 11, 31) };
        case SampleMetricsTimeRange.AllDates:
            return { start: allDatesFloor(), end: now };
        default:
            return null;
    }
}

const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;

export function rangeSpanYears(
    range: SampleMetricsTimeRange,
    customStartDate: string | null,
    customEndDate: string | null,
    now: Date = new Date()
): number | null {
    let start: Date;
    let end: Date;
    if (range === SampleMetricsTimeRange.Custom) {
        if (!customStartDate || !customEndDate) return null;
        start = new Date(customStartDate);
        end = new Date(customEndDate);
    } else {
        const resolved = resolveTimeRangeDates(range, now);
        if (!resolved) return null;
        start = resolved.start;
        end = resolved.end;
    }
    const span = (end.getTime() - start.getTime()) / MS_PER_YEAR;
    return isNaN(span) ? null : span;
}

export function finestGranularityFor(
    range: SampleMetricsTimeRange,
    customStartDate: string | null,
    customEndDate: string | null,
    now: Date = new Date()
): SampleMetricsGranularity {
    const years = rangeSpanYears(range, customStartDate, customEndDate, now);
    if (years == null) return SampleMetricsGranularity.Monthly;
    if (years > 5) return SampleMetricsGranularity.Yearly;
    if (years > 2) return SampleMetricsGranularity.Quarterly;
    return SampleMetricsGranularity.Monthly;
}

export function clampGranularity(
    selected: SampleMetricsGranularity,
    range: SampleMetricsTimeRange,
    customStartDate: string | null,
    customEndDate: string | null,
    now: Date = new Date()
): SampleMetricsGranularity {
    const floor = finestGranularityFor(range, customStartDate, customEndDate, now);
    return selected > floor ? selected : floor;
}

export function toNumberOrNull(value: unknown): number | null {
    if (value == null || value === '') return null;
    const num = Number(value);
    return isNaN(num) ? null : num;
}

export function toStringArray(value: unknown): string[] {
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

export function configToFilterValues(config: Record<string, unknown>): SampleCollectionMetricsFilterValues {
    return {
        selectedAgentGroupId: typeof config['selectedAgentGroupId'] === 'string' && config['selectedAgentGroupId'] !== '' ? config['selectedAgentGroupId'] : null,
        selectedStackBy: (toNumberOrNull(config['selectedStackBy']) as SampleMetricsStackBy | null) ?? SampleMetricsStackBy.Agent,
        selectedGranularity: (toNumberOrNull(config['selectedGranularity']) as SampleMetricsGranularity | null) ?? SampleMetricsGranularity.Monthly,
        selectedTimeRange: (toNumberOrNull(config['selectedTimeRange']) as SampleMetricsTimeRange | null) ?? SampleMetricsTimeRange.Ytd,
        customStartDate: typeof config['customStartDate'] === 'string' ? config['customStartDate'] : null,
        customEndDate: typeof config['customEndDate'] === 'string' ? config['customEndDate'] : null,
        selectedShiftType: toNumberOrNull(config['selectedShiftType']) as SampleShiftCoverage | null,
        selectedAgentIds: toStringArray(config['selectedAgentIds']),
        selectedSegIds: toStringArray(config['selectedSegIds']),
        selectedSurveyTypeIds: toStringArray(config['selectedSurveyTypeIds'])
    };
}

export const PivotSortColumn = {
    Name: 'name',
    Total: 'total',
    YoyChangePercent: 'yoyChangePercent'
} as const;

export type PivotSortColumn = typeof PivotSortColumn[keyof typeof PivotSortColumn];

export const DEFAULT_PIVOT_SORT_COLUMN: PivotSortColumn = PivotSortColumn.Name;
export const DEFAULT_PIVOT_SORT_ASC = true;

const PIVOT_SORT_COLUMN_VALUES: readonly string[] = Object.values(PivotSortColumn);

export function isPivotSortColumn(column: string): column is PivotSortColumn {
    return PIVOT_SORT_COLUMN_VALUES.includes(column);
}

export function filterValuesToConfig(values: SampleCollectionMetricsFilterValues): Record<string, unknown> {
    const config: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(values)) {
        config[key] = val;
    }
    return config;
}
